import React, { useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import electricianImage from "../assets/services/electrician.png";
import plumberImage from "../assets/services/plumber.png";
import painterImage from "../assets/services/painter.png";
import constructorImage from "../assets/services/constructor.png";
import carpenterImage from "../assets/services/carpenter.png";
import cleaningImage from "../assets/services/cleaning.png";
import appliancesRepairImage from "../assets/services/appliances-repair.png";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import { checkPincodeServiceability } from "../services/accountService";
import { createServiceRequest } from "../services/serviceRequestService";
import { fetchServiceBookingSettings } from "../services/serviceBookingSettingsService";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "../utils/toast";
import { normalizeIndianMobileNumber } from "../utils/phoneNumber";
import { readSelectedAddress, readSelectedAddressDisplay } from "../utils/selectedAddress";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import "../styles/pages/ServicesPage.css";
import "../styles/pages/AccountPageSpacing.css";

type WizardStep = 2 | 3 | 4 | 5;

const SERVICE_BOOKING_DRAFT_STORAGE_KEY = "eldoo-service-booking-draft";
const SERVICE_BOOKING_WIZARD_STATE_STORAGE_KEY = "eldoo-service-booking-wizard-state";
const getTodayDateValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type ServiceOption = {
  key: string;
  name: string;
  title: string;
  description: string;
  image: string;
};

const services: ServiceOption[] = [
  {
    key: "electrician",
    name: "Electrician",
    title: "Electrical wiring",
    description: "Safe repair and installation support for switches, wiring, and fixtures.",
    image: electricianImage,
  },
  {
    key: "plumber",
    name: "Plumber",
    title: "Plumbing",
    description: "Leak fixing, pipe fitting, tap replacement, and water flow troubleshooting.",
    image: plumberImage,
  },
  {
    key: "painter",
    name: "Painter",
    title: "Painting",
    description: "Interior and exterior painting services with neat surface preparation.",
    image: painterImage,
  },
  {
    key: "constructor",
    name: "Constructor",
    title: "Constructing",
    description: "Site-ready building and renovation help for structured improvement work.",
    image: constructorImage,
  },
  {
    key: "carpenter",
    name: "Carpenter",
    title: "Carpentering",
    description: "Woodwork repair, fittings, modular setup, and furniture installation.",
    image: carpenterImage,
  },
  {
    key: "cleaning",
    name: "Cleaning",
    title: "Cleaning",
    description: "Deep cleaning support for homes, work areas, and post-project cleanup.",
    image: cleaningImage,
  },
  {
    key: "appliances-repair",
    name: "Appliances Repair",
    title: "Repairing appliances",
    description: "Diagnosis and repair support for common home and utility appliances.",
    image: appliancesRepairImage,
  },
];

const preferredTimeSlot = "Any time";

type ServiceBookingErrors = Partial<Record<
  "preferredDate" | "customerName" | "phoneNumber" | "address" | "postalCode" | "description",
  string
>>;

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

type ServiceBookingDraft = {
  selectedServiceKey: string;
  customerName: string;
  phoneNumber: string;
  preferredDate: string;
  description: string;
};

type ServiceBookingWizardState = ServiceBookingDraft & {
  bookingOpen: boolean;
  bookingStep: WizardStep;
};

const readServiceBookingDraft = (): ServiceBookingDraft | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(SERVICE_BOOKING_DRAFT_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as ServiceBookingDraft;
  } catch {
    return null;
  }
};

const storeServiceBookingDraft = (draft: ServiceBookingDraft | null) => {
  if (typeof window === "undefined") {
    return;
  }

  if (draft) {
    window.sessionStorage.setItem(SERVICE_BOOKING_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    return;
  }

  window.sessionStorage.removeItem(SERVICE_BOOKING_DRAFT_STORAGE_KEY);
};

const readServiceBookingWizardState = (): ServiceBookingWizardState | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(SERVICE_BOOKING_WIZARD_STATE_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<ServiceBookingWizardState>;
    if (
      !parsed.bookingOpen ||
      !parsed.selectedServiceKey ||
      !parsed.bookingStep ||
      typeof parsed.customerName !== "string" ||
      typeof parsed.phoneNumber !== "string" ||
      typeof parsed.preferredDate !== "string" ||
      typeof parsed.description !== "string"
    ) {
      return null;
    }

    return {
      bookingOpen: true,
      bookingStep: parsed.bookingStep,
      selectedServiceKey: parsed.selectedServiceKey,
      customerName: parsed.customerName,
      phoneNumber: parsed.phoneNumber,
      preferredDate: parsed.preferredDate,
      description: parsed.description,
    };
  } catch {
    return null;
  }
};

const storeServiceBookingWizardState = (state: ServiceBookingWizardState | null) => {
  if (typeof window === "undefined") {
    return;
  }

  if (state) {
    window.sessionStorage.setItem(SERVICE_BOOKING_WIZARD_STATE_STORAGE_KEY, JSON.stringify(state));
    return;
  }

  window.sessionStorage.removeItem(SERVICE_BOOKING_WIZARD_STATE_STORAGE_KEY);
};

const ServicesPage: React.FC = () => {
  const { user, completeProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState<WizardStep>(2);
  const [selectedServiceKey, setSelectedServiceKey] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [preferredDate, setPreferredDate] = useState(getTodayDateValue());
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ServiceBookingErrors>({});
  const [serviceabilityPopupMessage, setServiceabilityPopupMessage] = useState<string | null>(null);
  const selectedAddress = readSelectedAddress(user);
  const selectedAddressDisplay = readSelectedAddressDisplay(user);
  const hasSavedProfile = Boolean(user?.fullName?.trim() && user?.phoneNumber?.trim());
  const [submittedRequest, setSubmittedRequest] = useState<{
    id: number;
    serviceName: string;
    customerName: string;
    phoneNumber: string;
    address: string;
    postalCode: string;
    preferredDate: string;
    preferredTimeSlot: string;
    description: string;
    status: string;
  } | null>(null);

  useBodyScrollLock(Boolean(bookingOpen || serviceabilityPopupMessage));

  const activeService = useMemo(
    () => services.find((service) => service.key === selectedServiceKey) ?? null,
    [selectedServiceKey],
  );

  useEffect(() => {
    if (bookingOpen) {
      setCustomerName(user?.fullName || "");
      setPhoneNumber(user?.phoneNumber || "");
    }
  }, [bookingOpen, user]);

  useEffect(() => {
    let mounted = true;

    fetchServiceBookingSettings()
      .then((settings) => {
        if (mounted) {
          setBookingEnabled(settings.enableServiceBooking);
        }
      })
      .catch(() => {
        if (mounted) {
          setBookingEnabled(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const persistedWizardState = readServiceBookingWizardState();
    if (persistedWizardState) {
      setBookingOpen(true);
      setBookingStep(persistedWizardState.bookingStep);
      setSelectedServiceKey(persistedWizardState.selectedServiceKey);
      setCustomerName(persistedWizardState.customerName);
      setPhoneNumber(persistedWizardState.phoneNumber);
      setPreferredDate(persistedWizardState.preferredDate);
      setDescription(persistedWizardState.description);
      setSubmitting(false);
      setFieldErrors({});
      setServiceabilityPopupMessage(null);
      return;
    }

    const locationState = location.state as { resumeServiceBooking?: boolean; resumeServiceBookingStep?: WizardStep } | null;
    if (!locationState?.resumeServiceBooking) {
      return;
    }

    const draft = readServiceBookingDraft();
    if (!draft) {
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    setBookingOpen(true);
    setBookingStep(locationState.resumeServiceBookingStep ?? 3);
    setSelectedServiceKey(draft.selectedServiceKey);
    setCustomerName(draft.customerName);
    setPhoneNumber(draft.phoneNumber);
    setPreferredDate(draft.preferredDate);
    setDescription(draft.description);
    setSubmitting(false);
    setFieldErrors({});
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!bookingOpen || !selectedServiceKey) {
      return;
    }

    storeServiceBookingWizardState({
      bookingOpen: true,
      bookingStep,
      selectedServiceKey,
      customerName,
      phoneNumber,
      preferredDate,
      description,
    });
  }, [bookingOpen, bookingStep, selectedServiceKey, customerName, phoneNumber, preferredDate, description]);

  const resetBookingForm = () => {
    storeServiceBookingDraft(null);
    storeServiceBookingWizardState(null);
    setBookingOpen(false);
    setBookingStep(2);
    setSelectedServiceKey(null);
    setCustomerName("");
    setPhoneNumber("");
    setPreferredDate(getTodayDateValue());
    setDescription("");
    setSubmitting(false);
    setFieldErrors({});
    setSubmittedRequest(null);
    setServiceabilityPopupMessage(null);
  };

  const openBookingWizard = (serviceKey: string) => {
    setBookingOpen(true);
    setBookingStep(2);
    setSelectedServiceKey(serviceKey);
    setCustomerName(user?.fullName || "");
    setPhoneNumber(user?.phoneNumber || "");
    setPreferredDate(getTodayDateValue());
    setDescription("");
    setSubmitting(false);
    setFieldErrors({});
    setSubmittedRequest(null);
    setServiceabilityPopupMessage(null);
    storeServiceBookingDraft(null);
    storeServiceBookingWizardState({
      bookingOpen: true,
      bookingStep: 2,
      selectedServiceKey: serviceKey,
      customerName: user?.fullName || "",
      phoneNumber: user?.phoneNumber || "",
      preferredDate: getTodayDateValue(),
      description: "",
    });
  };

  const validateStep2 = () => {
    const nextErrors: ServiceBookingErrors = {};
    if (!preferredDate.trim()) {
      nextErrors.preferredDate = "* required";
    }
    setFieldErrors((current) => ({ ...current, ...nextErrors, preferredDate: nextErrors.preferredDate }));
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep3 = () => {
    const hasSelectedAddress = Boolean(selectedAddress?.streetAddress?.trim() && selectedAddress?.postalCode?.trim());
    if (!hasSelectedAddress) {
      setFieldErrors((current) => ({ ...current, address: "* required" }));
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    const nextErrors: ServiceBookingErrors = {};
    if (!customerName.trim()) {
      nextErrors.customerName = "* required";
    }
    if (!phoneNumber.trim()) {
      nextErrors.phoneNumber = "* required";
    }
    if (!description.trim()) {
      nextErrors.description = "* required";
    }
    setFieldErrors((current) => ({ ...current, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep5 = () => {
    const nextErrors: ServiceBookingErrors = {};
    if (!selectedAddress?.streetAddress?.trim()) {
      nextErrors.address = "* required";
    }
    if (!selectedAddress?.postalCode?.trim()) {
      nextErrors.postalCode = "* required";
    }
    setFieldErrors((current) => ({ ...current, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!activeService) {
      return;
    }

    if (!validateStep2() || !validateStep3() || !validateStep4() || !validateStep5()) {
      toast.error("Please complete the required fields.");
      return;
    }

    const sanitizedPostalCode = (selectedAddress?.postalCode?.trim() || "").replace(/\D/g, "").slice(0, 6);
    try {
      const serviceability = await checkPincodeServiceability(sanitizedPostalCode);
      if (!serviceability.serviceable) {
        const popupMessage = serviceability.message || "Service is not available at your area.";
        setFieldErrors((current) => ({
          ...current,
          postalCode: popupMessage,
        }));
        setServiceabilityPopupMessage(popupMessage);
        toast.error(popupMessage);
        return;
      }
    } catch {
      const popupMessage = "Service is not available at your area.";
      setFieldErrors((current) => ({
        ...current,
        postalCode: popupMessage,
      }));
      setServiceabilityPopupMessage(popupMessage);
      toast.error(popupMessage);
      return;
    }

    setSubmitting(true);
    try {
      const sanitizedCustomerName = customerName.trim().replace(/\s+/g, " ");
      const sanitizedPhoneNumber = normalizeIndianMobileNumber(phoneNumber);

      if (!hasSavedProfile) {
        const profileResult = await completeProfile({
          fullName: sanitizedCustomerName,
          phoneNumber: sanitizedPhoneNumber,
        });
        if (profileResult.error) {
          toast.error(profileResult.error);
          setSubmitting(false);
          return;
        }
      }

      const sanitizedAddress = selectedAddress?.streetAddress?.trim() || "";
      const sanitizedDescription = description.trim();

      const request = await createServiceRequest({
        serviceKey: activeService.key,
        serviceName: activeService.name,
        customerName: sanitizedCustomerName,
        phoneNumber: sanitizedPhoneNumber,
        address: sanitizedAddress,
        postalCode: sanitizedPostalCode,
        preferredDate: preferredDate.trim(),
        preferredTimeSlot,
        description: sanitizedDescription,
        problemImages: [],
      });
      setSubmittedRequest({
        id: request.id,
        serviceName: request.serviceName,
        customerName: request.customerName,
        phoneNumber: request.phoneNumber,
        address: request.address,
        postalCode: request.postalCode,
        preferredDate: request.preferredDate,
        preferredTimeSlot: request.preferredTimeSlot,
        description: request.description,
        status: request.status,
      });
      setSubmitting(false);
      toast.success("Service request submitted. Eldoo team will contact you soon.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to submit the service request right now."));
      setSubmitting(false);
    }
  };

  const bookingTitle =
    bookingStep === 2
      ? "Select next date"
      : bookingStep === 3
        ? "Choose address"
        : bookingStep === 4
          ? "Issue description"
          : "Review request";

  const openAddressPage = (mode: "saved" | "new") => {
    storeServiceBookingDraft({
      selectedServiceKey: selectedServiceKey || "",
      customerName,
      phoneNumber,
      preferredDate,
      description,
    });
    navigate("/address", {
      state: {
        returnTo: "/services",
        source: "service-booking",
        resumeServiceBookingStep: 3,
        openLocationPickerFirst: mode === "new",
      },
    });
  };

  return (
    <section className="shell section page-section services-page">
      <MobilePageHeader title="Book a Service" />

      <div className="page-header services-page__header">
        <span className="eyebrow">Services</span>
        <h1>Book reliable field services</h1>
      </div>

      <div className="services-page__cards">
        {services.map((service) => (
          <article key={service.key} className="services-page__card">
            <div className="services-page__card-media">
              <img src={service.image} alt={service.name} className="services-page__card-image" />
            </div>
            <div className="services-page__card-content">
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <button
                type="button"
                className={`services-page__book-button ${bookingEnabled ? "" : "is-coming-soon"}`}
                onClick={() => {
                  if (bookingEnabled) {
                    openBookingWizard(service.key);
                  }
                }}
                aria-disabled={!bookingEnabled}
              >
                {bookingEnabled ? "Book now" : "Coming soon"}
              </button>
            </div>
          </article>
        ))}
      </div>

      {bookingOpen ? (
        <div className="services-booking-modal" role="presentation">
          <div
            className="services-booking-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="service-booking-title"
            onClick={(event) => event.stopPropagation()}
          >
                <div className="services-booking-modal__header">
                  <div>
                    <span className="eyebrow">Book service</span>
                    <h2 id="service-booking-title">{bookingTitle}</h2>
                  </div>
                  <button type="button" className="services-booking-modal__close" onClick={resetBookingForm}>
                    ×
                  </button>
            </div>

            {serviceabilityPopupMessage ? (
              <div className="services-booking-modal__notice-overlay" role="presentation">
                <div
                  className="services-booking-modal__notice"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="serviceability-popup-title"
                  onClick={(event) => event.stopPropagation()}
                >
                  <span className="eyebrow">Service check</span>
                  <h3 id="serviceability-popup-title">Service not available</h3>
                  <p>{serviceabilityPopupMessage}</p>
                  <button type="button" className="button" onClick={() => setServiceabilityPopupMessage(null)}>
                    OK
                  </button>
                </div>
              </div>
            ) : null}

            <div className="services-booking-modal__body">
              {submittedRequest ? (
                <div className="services-booking-success">
                  <div className="services-booking-success__card">
                    <div className="services-booking-success__header">
                      <h3>Service request submitted successfully</h3>
                    </div>

                    <div className="services-booking-success__actions">
                      <button type="button" className="link-button" onClick={resetBookingForm}>
                        Book another service
                      </button>
                      <Link className="button" to="/service-tracking" onClick={resetBookingForm}>
                        Track service
                      </Link>
                    </div>
                  </div>
                </div>
              ) : null}

              {!submittedRequest && bookingStep === 2 ? (
                <form className="services-booking-form" onSubmit={(event) => event.preventDefault()}>
                  <div className="services-booking-modal__selected-service">
                    <div className="services-booking-modal__selected-service-card">
                      <img src={activeService?.image ?? ""} alt={activeService?.name ?? "Selected service"} />
                      <div>
                        <strong>{activeService?.name}</strong>
                        <span>{activeService?.title}</span>
                        <p>{activeService?.description}</p>
                      </div>
                    </div>
                  </div>
                  <label className="services-booking-form__field">
                    <span>Date</span>
                    <input
                      type="date"
                      value={preferredDate}
                      onChange={(event) => setPreferredDate(event.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                    {fieldErrors.preferredDate ? <small className="form-error">{fieldErrors.preferredDate}</small> : null}
                  </label>

                  <div className="services-booking-form__actions">
                    <button
                      type="button"
                      className="link-button"
                      onClick={resetBookingForm}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        if (validateStep2()) {
                          setBookingStep(3);
                        } else {
                          toast.error("Please select a date.");
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                </form>
              ) : null}

              {!submittedRequest && bookingStep === 3 ? (
                <form className="services-booking-form" onSubmit={(event) => event.preventDefault()}>
                  <div className="services-booking-form__address-step">
                    <div className="services-booking-form__address-step-card">
                      <span>Address is managed from your address page.</span>
                      <div className="services-booking-form__address-step-actions">
                        <button type="button" className="button button--light" onClick={() => openAddressPage("saved")}>
                          Select saved address
                        </button>
                        <button type="button" className="button" onClick={() => openAddressPage("new")}>
                          Add new address
                        </button>
                      </div>
                    </div>

                    {selectedAddressDisplay ? (
                      <div className="services-booking-form__selected-address-summary">
                        <span>Selected address</span>
                        <strong>{selectedAddressDisplay}</strong>
                      </div>
                    ) : (
                      <div className="services-booking-form__empty-state">
                        No address selected yet. Use the address page to pick or add one.
                      </div>
                    )}
                    {fieldErrors.address ? <small className="form-error">{fieldErrors.address}</small> : null}
                  </div>

                  <div className="services-booking-form__actions">
                    <button type="button" className="link-button" onClick={() => setBookingStep(2)}>
                      Back
                    </button>
                    <button className="button" type="button" onClick={() => {
                      if (validateStep3()) {
                        setBookingStep(4);
                      } else {
                        toast.error("Please complete the required fields.");
                      }
                    }}>
                      Review request
                    </button>
                  </div>
                </form>
              ) : null}

              {!submittedRequest && bookingStep === 4 ? (
                <form className="services-booking-form" onSubmit={(event) => event.preventDefault()}>
                  <div className="services-booking-form__review">
                    <article className="services-booking-form__review-card">
                      <div className="services-booking-form__review-service">
                        {activeService ? (
                          <>
                            <img src={activeService.image} alt={activeService.name} />
                            <div>
                              <strong>{activeService.name}</strong>
                              <span>{activeService.title}</span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </article>

                    {hasSavedProfile ? (
                      <div className="services-booking-form__profile-summary">
                        <div>
                          <span>Name</span>
                          <strong>{customerName || "Not entered"}</strong>
                        </div>
                        <div>
                          <span>Mobile number</span>
                          <strong>{phoneNumber || "Not entered"}</strong>
                        </div>
                      </div>
                    ) : (
                      <div className="services-booking-form__grid">
                        <label className="services-booking-form__field">
                          <span>Name</span>
                          <input
                            value={customerName}
                            onChange={(event) => setCustomerName(event.target.value)}
                            placeholder="Enter your full name"
                            required
                          />
                          {fieldErrors.customerName ? <small className="form-error">{fieldErrors.customerName}</small> : null}
                        </label>

                        <label className="services-booking-form__field">
                          <span>Mobile number</span>
                          <input
                            value={phoneNumber}
                            onChange={(event) => setPhoneNumber(normalizeIndianMobileNumber(event.target.value))}
                            onPaste={(event) => {
                              event.preventDefault();
                              setPhoneNumber(normalizeIndianMobileNumber(event.clipboardData.getData("text")));
                            }}
                            placeholder="10 digit mobile number"
                            inputMode="numeric"
                            pattern="\d{10}"
                            required
                          />
                          {fieldErrors.phoneNumber ? <small className="form-error">{fieldErrors.phoneNumber}</small> : null}
                        </label>
                      </div>
                    )}

                    <label className="services-booking-form__field services-booking-form__field--wide">
                      <span>Issue description</span>
                      <textarea
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="Describe the issue, repair need, or installation details."
                        rows={5}
                        required
                      />
                      {fieldErrors.description ? <small className="form-error">{fieldErrors.description}</small> : null}
                    </label>
                  </div>

                  <div className="services-booking-form__actions">
                    <button type="button" className="link-button" onClick={() => setBookingStep(3)}>
                      Back
                    </button>
                    <button
                      className="button"
                      type="button"
                      onClick={() => {
                        if (validateStep4()) {
                          setBookingStep(5);
                        } else {
                          toast.error("Please complete the required fields.");
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                </form>
              ) : null}

              {!submittedRequest && bookingStep === 5 ? (
                <form className="services-booking-form" onSubmit={(event) => event.preventDefault()}>
                  <div className="services-booking-form__review">
                    <article className="services-booking-form__review-card">
                      <div className="services-booking-form__review-service">
                        {activeService ? (
                          <>
                            <img src={activeService.image} alt={activeService.name} />
                            <div>
                              <strong>{activeService.name}</strong>
                              <span>{activeService.title}</span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </article>

                    <div className="services-booking-form__review-grid">
                      <div>
                        <span>Name</span>
                        <strong>{customerName || "Not entered"}</strong>
                      </div>
                      <div>
                        <span>Mobile number</span>
                        <strong>{phoneNumber || "Not entered"}</strong>
                      </div>
                      <div>
                        <span>Preferred date</span>
                        <strong>{preferredDate || "Not selected"}</strong>
                      </div>
                      <div>
                        <span>Time slot</span>
                        <strong>{preferredTimeSlot}</strong>
                      </div>
                      <div className="services-booking-form__review-full">
                        <span>Address</span>
                        <strong>{selectedAddressDisplay || "Not entered"}</strong>
                      </div>
                      <div>
                        <span>Postal code</span>
                        <strong>{selectedAddress?.postalCode?.trim() || "Not entered"}</strong>
                      </div>
                      <div className="services-booking-form__review-full">
                        <span>Issue description</span>
                        <strong>{description || "Not entered"}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="services-booking-form__actions">
                    <button type="button" className="link-button" onClick={() => setBookingStep(4)}>
                      Back
                    </button>
                    <button
                      className={`button services-booking-form__submit-button ${submitting ? "is-loading" : ""}`}
                      type="button"
                      disabled={
                        submitting ||
                        !activeService ||
                        !preferredDate ||
                        !customerName.trim() ||
                        !phoneNumber.trim() ||
                        !description.trim() ||
                        !selectedAddress?.streetAddress?.trim() ||
                        !selectedAddress?.postalCode?.trim()
                      }
                      aria-busy={submitting}
                      onClick={() => void handleSubmit()}
                    >
                      {submitting ? (
                        <span className="button-loading">
                          <span className="button-loading__spinner" aria-hidden="true" />
                          <span>Submitting...</span>
                        </span>
                      ) : (
                        "Submit request"
                      )}
                    </button>
                  </div>
                </form>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ServicesPage;
